import React, { useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import AppRoutes from './routes';
import ErrorBoundary from './shared/components/ErrorBoundary';
import OfflineBanner from './shared/components/OfflineBanner';
import { useAppStore } from './shared/stores/appStore';
import useNativeSetup, { applyStatusBarStyle } from './shared/hooks/useNativeSetup';
import { useAttributionCapture } from './shared/hooks/useAttributionCapture';
import useIAPSetup from './shared/hooks/useIAPSetup';
import useSyncEngine from './services/sync/engine';

const isCapacitor = window.location.protocol === 'capacitor:';
const webOrigin = window.location.origin;
const redirectUrl = isCapacitor
  ? 'capacitor://localhost/auth/callback'
  : `${webOrigin}/auth/callback`;
const logoutUrl = isCapacitor
  ? 'capacitor://localhost/login'
  : `${webOrigin}/login`;

// On native, open OAuth URLs in an in-app browser (SFSafariViewController)
// instead of the external system browser, per App Store Guideline 4.
async function capacitorUrlOpener(url, redirectSignIn) {
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, windowName: '_self' });
}

Amplify.configure({
  Auth: {
    region: process.env.REACT_APP_AWS_REGION || 'eu-west-2',
    userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID || '',
    oauth: {
      domain: process.env.REACT_APP_COGNITO_DOMAIN || 'babytrack-auth.auth.eu-west-2.amazoncognito.com',
      scope: ['openid', 'email', 'profile'],
      redirectSignIn: redirectUrl,
      redirectSignOut: logoutUrl,
      responseType: 'code',
      urlOpener: Capacitor.isNativePlatform() ? capacitorUrlOpener : undefined,
    },
  },
  API: {
    endpoints: [
      {
        name: 'babytrackApi',
        endpoint: process.env.REACT_APP_API_ENDPOINT || '',
        region: process.env.REACT_APP_AWS_REGION || 'eu-west-2',
      },
    ],
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const theme = useAppStore((s) => s.theme);
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);
  const updateLastActiveAt = useAppStore((s) => s.updateLastActiveAt);

  useNativeSetup();
  // Apple Ads attribution: mints the AdServices token at first launch (signed
  // out too) and exchanges it through the backend once a session exists.
  useAttributionCapture();

  // Theme is tri-state ('system' | 'light' | 'dark'); `nightMode` is the
  // resolved boolean the whole app reads. 'system' is resolved from
  // matchMedia ON MOUNT (a phone already in dark mode used to open the app in
  // light and stay there until the system flipped) and the listener is
  // attached ONLY while the choice is 'system', so an explicit Light/Dark
  // pick is never overridden at sunset.
  useEffect(() => {
    if (theme === 'dark') {
      setNightMode(true);
      return undefined;
    }
    if (theme === 'light') {
      setNightMode(false);
      return undefined;
    }
    let mq;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      setNightMode(false);
      return undefined;
    }
    setNightMode(mq.matches);
    const handler = (e) => setNightMode(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, setNightMode]);

  // Sync dark class to <html> so body and all dark: utilities work, and keep
  // the native status bar in step with the resolved theme.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', nightMode);
    applyStatusBarStyle(nightMode);
  }, [nightMode]);

  // Update last active timestamp (copies the previous value to
  // previousActiveAt first, which is what the dashboard handoff compares against)
  useEffect(() => {
    updateLastActiveAt();
  }, [updateLastActiveAt]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <IAPSetup />
        <SyncEngine />
        <OfflineBanner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        {/* Bottom-centre, clear of the 64px tab bar + safe area: toasts used to
            land on the header controls (and on the offline strip) at the far
            end of the screen from the thumb that triggered them. */}
        <Toaster
          position="bottom-center"
          containerStyle={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)' }}
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: '14px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 500,
              maxWidth: '92vw',
              background: 'var(--toast-bg)',
              color: 'var(--toast-fg)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
              border: '1px solid var(--toast-border)',
            },
            success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
          }}
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function IAPSetup() {
  useIAPSetup();
  return null;
}

function SyncEngine() {
  useSyncEngine();
  return null;
}

export default App;
